import { useEffect, useRef } from "react";
import * as THREE from "three";

const createMaterial = (color, options = {}) =>
  new THREE.MeshStandardMaterial({
    color,
    roughness: 0.44,
    metalness: 0.18,
    ...options,
  });

const ChargerScene3D = ({ className = "", variant = "dashboard" }) => {
  const mountRef = useRef(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return undefined;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(38, 1, 0.1, 100);
    camera.position.set(5.4, 3.2, 6.2);
    camera.lookAt(0, 0.25, 0);

    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      preserveDrawingBuffer: true,
      powerPreference: "high-performance",
    });
    renderer.setClearAlpha(0);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    mount.appendChild(renderer.domElement);

    const group = new THREE.Group();
    scene.add(group);

    const baseMaterial = createMaterial("#06182f");
    const cabinetMaterial = createMaterial("#eef4ef", {
      roughness: 0.34,
      metalness: 0.08,
    });
    const sideMaterial = createMaterial("#083764");
    const screenMaterial = new THREE.MeshBasicMaterial({ color: "#03182f" });
    const glowMaterial = new THREE.MeshBasicMaterial({ color: "#22d3ee" });
    const amberMaterial = new THREE.MeshBasicMaterial({ color: "#f2b84b" });
    const darkMaterial = createMaterial("#06182f");

    const base = new THREE.Mesh(new THREE.BoxGeometry(5.4, 0.2, 3), baseMaterial);
    base.position.y = -0.9;
    group.add(base);

    const grid = new THREE.GridHelper(7, 16, "#22d3ee", "#0b5fa5");
    grid.position.y = -0.78;
    grid.material.transparent = true;
    grid.material.opacity = 0.2;
    group.add(grid);

    const cabinet = new THREE.Mesh(
      new THREE.BoxGeometry(1.25, 2.7, 0.9),
      cabinetMaterial,
    );
    cabinet.position.set(0, 0.52, 0);
    group.add(cabinet);

    const cabinetSide = new THREE.Mesh(
      new THREE.BoxGeometry(0.18, 2.55, 0.96),
      sideMaterial,
    );
    cabinetSide.position.set(-0.72, 0.49, 0);
    group.add(cabinetSide);

    const topCap = new THREE.Mesh(
      new THREE.BoxGeometry(1.45, 0.24, 1.05),
      createMaterial("#dff7ff"),
    );
    topCap.position.set(0, 1.98, 0);
    group.add(topCap);

    const screen = new THREE.Mesh(
      new THREE.BoxGeometry(0.78, 0.5, 0.04),
      screenMaterial,
    );
    screen.position.set(0.15, 1.28, 0.48);
    group.add(screen);

    const screenGlow = new THREE.Mesh(
      new THREE.BoxGeometry(0.52, 0.08, 0.045),
      glowMaterial,
    );
    screenGlow.position.set(0.15, 1.29, 0.51);
    group.add(screenGlow);

    const statusLight = new THREE.Mesh(
      new THREE.SphereGeometry(0.08, 24, 16),
      glowMaterial,
    );
    statusLight.position.set(0.5, 0.82, 0.51);
    group.add(statusLight);

    const leftDock = new THREE.Mesh(
      new THREE.BoxGeometry(0.35, 0.72, 0.28),
      darkMaterial,
    );
    leftDock.position.set(-1.35, 0.36, 0.16);
    group.add(leftDock);

    const rightDock = leftDock.clone();
    rightDock.position.set(1.35, 0.36, 0.16);
    group.add(rightDock);

    const plugGeometry = new THREE.CylinderGeometry(0.08, 0.08, 0.42, 18);
    const plugA = new THREE.Mesh(plugGeometry, amberMaterial);
    plugA.rotation.z = Math.PI / 2;
    plugA.position.set(-1.07, 0.7, 0.38);
    group.add(plugA);

    const plugB = plugA.clone();
    plugB.position.set(1.07, 0.68, 0.38);
    group.add(plugB);

    const cableMaterial = new THREE.MeshStandardMaterial({
      color: "#03182f",
      roughness: 0.62,
      metalness: 0.04,
    });

    const cableCurveA = new THREE.CatmullRomCurve3([
      new THREE.Vector3(-0.55, 0.95, 0.42),
      new THREE.Vector3(-1.05, 0.75, 0.86),
      new THREE.Vector3(-1.45, 0.15, 0.74),
      new THREE.Vector3(-1.2, -0.35, 0.28),
    ]);
    const cableA = new THREE.Mesh(
      new THREE.TubeGeometry(cableCurveA, 36, 0.035, 8, false),
      cableMaterial,
    );
    group.add(cableA);

    const cableCurveB = new THREE.CatmullRomCurve3([
      new THREE.Vector3(0.55, 0.92, 0.42),
      new THREE.Vector3(1.05, 0.76, 0.9),
      new THREE.Vector3(1.45, 0.16, 0.78),
      new THREE.Vector3(1.22, -0.33, 0.28),
    ]);
    const cableB = new THREE.Mesh(
      new THREE.TubeGeometry(cableCurveB, 36, 0.035, 8, false),
      cableMaterial,
    );
    group.add(cableB);

    const railMaterial = new THREE.MeshBasicMaterial({
      color: "#22d3ee",
      transparent: true,
      opacity: 0.55,
    });
    const rails = [];
    for (let index = 0; index < 3; index += 1) {
      const rail = new THREE.Mesh(
        new THREE.BoxGeometry(0.035, 0.035, 2.35),
        railMaterial.clone(),
      );
      rail.position.set(-2.15 + index * 2.15, -0.62, 0.05);
      rail.rotation.y = Math.PI / 2;
      rails.push(rail);
      group.add(rail);
    }

    const particles = Array.from({ length: 12 }).map((_, index) => {
      const particle = new THREE.Mesh(
        new THREE.SphereGeometry(0.045, 16, 12),
        index % 3 === 0 ? amberMaterial.clone() : glowMaterial.clone(),
      );
      particle.position.set(
        -2.3 + (index % 6) * 0.9,
        -0.52,
        -1.1 + Math.floor(index / 6) * 2.2,
      );
      group.add(particle);
      return particle;
    });

    const keyLight = new THREE.DirectionalLight("#ffffff", 2.1);
    keyLight.position.set(3.8, 4.5, 5.2);
    scene.add(keyLight);

    const fillLight = new THREE.DirectionalLight("#67ddf2", 0.75);
    fillLight.position.set(-5, 2, -2);
    scene.add(fillLight);

    scene.add(new THREE.AmbientLight("#dff7ff", 0.68));

    group.rotation.set(-0.06, variant === "auth" ? -0.42 : -0.24, 0);
    group.position.y = variant === "auth" ? -0.12 : 0;

    let pointerX = 0;
    let pointerY = 0;
    let frameId = 0;
    const clock = new THREE.Clock();

    const setSize = () => {
      const width = mount.clientWidth || 640;
      const height = mount.clientHeight || 360;
      renderer.setSize(width, height, false);
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
    };

    const resizeObserver = new ResizeObserver(setSize);
    resizeObserver.observe(mount);
    setSize();

    const handlePointerMove = (event) => {
      const bounds = mount.getBoundingClientRect();
      pointerX = ((event.clientX - bounds.left) / bounds.width - 0.5) * 0.5;
      pointerY = ((event.clientY - bounds.top) / bounds.height - 0.5) * 0.35;
    };

    const handlePointerLeave = () => {
      pointerX = 0;
      pointerY = 0;
    };

    mount.addEventListener("pointermove", handlePointerMove);
    mount.addEventListener("pointerleave", handlePointerLeave);

    const animate = () => {
      const elapsed = clock.getElapsedTime();
      const targetRotationY = (variant === "auth" ? -0.42 : -0.24) + pointerX;
      group.rotation.y += (targetRotationY - group.rotation.y) * 0.06;
      group.rotation.x = -0.06 + pointerY;

      statusLight.scale.setScalar(1 + Math.sin(elapsed * 3) * 0.16);
      screenGlow.scale.x = 1 + Math.sin(elapsed * 2.4) * 0.12;

      particles.forEach((particle, index) => {
        particle.position.x = -2.4 + ((elapsed * 0.85 + index * 0.54) % 4.8);
        particle.position.y = -0.51 + Math.sin(elapsed * 2 + index) * 0.045;
      });

      rails.forEach((rail, index) => {
        rail.material.opacity = 0.35 + Math.sin(elapsed * 1.6 + index) * 0.12;
      });

      renderer.render(scene, camera);
      frameId = window.requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.cancelAnimationFrame(frameId);
      resizeObserver.disconnect();
      mount.removeEventListener("pointermove", handlePointerMove);
      mount.removeEventListener("pointerleave", handlePointerLeave);
      renderer.dispose();
      scene.traverse((object) => {
        if (object.geometry) object.geometry.dispose();
        if (object.material) {
          if (Array.isArray(object.material)) {
            object.material.forEach((material) => material.dispose());
          } else {
            object.material.dispose();
          }
        }
      });
      if (renderer.domElement.parentNode === mount) {
        mount.removeChild(renderer.domElement);
      }
    };
  }, [variant]);

  return (
    <div
      ref={mountRef}
      className={`charger-scene-3d ${className}`}
      aria-hidden="true"
    />
  );
};

export default ChargerScene3D;
